import { useNavigate } from 'react-router-dom'

import { Avatar, Box, Button, Divider, Icon, Text } from '@factorialco/deprecated-design-system'
import { WhoIsInPopoverDataDocument } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_PERIOD_TYPE } from 'modules/attendance/components/TimeTracking/Layout/form'
import { shiftLocationTagAttributes } from 'modules/attendance/lib/shiftLocationTag'

import ListPopoverItem from './ListPopoverItem'
import PlannedTimes from './PlannedTimes'
import { getStatusBadge } from './utils'

const convertSecondsToReadableFormat = (seconds: number) => {
  const hour = Math.floor(seconds / 3600)
  const min = Math.floor((seconds % 3600) / 60)

  if (hour > 0) {
    return i18n.t('dashboard.who_is_in.tooltip.hour_mins_late', { hour, min })
  } else {
    return i18n.t('dashboard.who_is_in.tooltip.only_mins_late', { min })
  }
}

interface Props {
  employeeId: number
  togglePopover: () => void
  toggleEmailReminderModalOpen?: (value: boolean) => () => void
  showButtons?: boolean
}

const ListPopover = ({
  employeeId,
  toggleEmailReminderModalOpen,
  togglePopover,
  showButtons,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { loading, data } = useQuery(WhoIsInPopoverDataDocument, {
    variables: {
      endOn: dateToString(moment(), 'backend'),
      startOn: dateToString(moment(), 'backend'),
      employeeId,
    },
  })

  if (loading || !data) {
    return (
      <Box width="s240" height="s300" alignItems="center" justifyContent="center">
        <Loading />
      </Box>
    )
  }

  const status = data.timeInsights.clockInStatusesConnection.nodes[0]

  if (!status) {
    throw new Error('No clock in status found')
  }

  const {
    status: type,
    plannedClockIn,
    plannedClockOut,
    employee,
    shift,
    latenessInSeconds,
    employeeTimezone,
  } = status
  const hasPlannedTime = plannedClockIn && plannedClockOut
  const fullName = employee?.fullName

  const handleEmailReminder = () => {
    toggleEmailReminderModalOpen && toggleEmailReminderModalOpen(true)()
    togglePopover()
    track(trackingEvents.timeTracking.sendClockInReminderStart, {
      from: 'widget',
      user_count: 1,
    })
  }

  const navigateToTTPage = () => {
    const now = new Date()
    togglePopover()
    track(trackingEvents.timeTracking.goToEmployeeClockIn, { from: 'rta-widget' })

    return navigate(
      navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
        day: now.getDate(),
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        periodType: DEFAULT_PERIOD_TYPE,
        employeeId,
      })
    )
  }

  const lateness = convertSecondsToReadableFormat(latenessInSeconds)

  const badge = getStatusBadge(type ?? 'invalid_status')

  const workplaceName = shift?.workplace?.name

  const { textLabel: shiftLocationLabel } = shiftLocationTagAttributes({
    hasLocationIssueAndGeofencing: false,
    outOfRadiusIssue: false,
    locationType: shift?.locationType ?? '',
    workplaceName,
    hasShiftLocations: false,
    hasGeolocation: false,
    hasGeofencing: false,
    hasLocation: true, // In WhoIsIn, we support currently only the work location
  })

  return (
    <Box justifyContent="center" gap="s12">
      <Box paddingBottom="s12" justifyContent="spaceAround" gap="s16">
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Avatar
            size="s32"
            url={buildUrl(employee?.access?.avatar ?? '')}
            label={fullName ?? ''}
            badge={badge}
          />
          <Box
            textAlign="right"
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            height="fit-content"
            gap="s4"
            minWidth="fit-content"
          >
            {latenessInSeconds > 0 && (
              <>
                <Box width="s16" height="s16">
                  <Icon.Small icon="clock" color="grey000White" />
                </Box>
                <Text.Normal color="grey000White" size="200" weight="semibold">
                  {lateness}
                </Text.Normal>
              </>
            )}
          </Box>
        </Box>
        <Box textAlign="left" gap="s4">
          <Box>
            <Text.Normal color="grey000White" size="200" weight="medium">
              {fullName}
            </Text.Normal>
            <Text.Normal color="grey000White" size="200" weight="medium">
              {employee?.referenceContract?.jobTitle ?? ''}
            </Text.Normal>
          </Box>
        </Box>
        <Divider backgroundColor="grey000White" />
        <Box gap="s4">
          {hasPlannedTime && (
            <PlannedTimes
              plannedClockIn={plannedClockIn}
              plannedClockOut={plannedClockOut}
              employeeTimezone={employeeTimezone}
            />
          )}
          {shift?.locationType && (
            <ListPopoverItem
              term={i18n.t('dashboard.who_is_in.tooltip.location')}
              details={shiftLocationLabel}
            />
          )}
          <ListPopoverItem
            term={i18n.t('dashboard.who_is_in.tooltip.manager')}
            details={employee?.manager?.fullName || ''}
          />
        </Box>
      </Box>
      {!!showButtons && (
        <Box
          flexDirection="row"
          gap="s8"
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
        >
          {type === 'missing_clock_in' && (
            <Button type="secondary-dark" onClick={handleEmailReminder}>
              {i18n.t('dashboard.who_is_in.tooltip.send_reminder')}
            </Button>
          )}
          <Button type="secondary-dark" onClick={navigateToTTPage}>
            {i18n.t('dashboard.who_is_in.tooltip.view_clock_in')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ListPopover
